/**
 * Campos de formulário no visual das prévias (rótulo em caixa alta acima do campo).
 * São componentes controlados: value + onChange.
 */
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

interface Wrap {
  label: ReactNode;
  hint?: ReactNode;
  /** ocupa as duas colunas da grade do formulário */
  wide?: boolean;
  /** destaque quando o campo foi preenchido automaticamente */
  filled?: boolean;
  className?: string;
}

function cls(w: Wrap) {
  return `field${w.wide ? ' span2' : ''}${w.filled ? ' filled' : ''}${w.className ? ` ${w.className}` : ''}`;
}

export function TextField({
  label,
  hint,
  wide,
  filled,
  className,
  onValue,
  ...input
}: Wrap & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & { onValue?: (v: string) => void }) {
  return (
    <label className={cls({ label, wide, filled, className })}>
      <span>{label}</span>
      <input {...input} onChange={e => (onValue ? onValue(e.target.value) : input.onChange?.(e))} />
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function NumberField({
  label,
  hint,
  wide,
  className,
  value,
  onValue,
  ...input
}: Wrap &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'value' | 'onChange'> & {
    value: number;
    onValue: (v: number) => void;
  }) {
  return (
    <label className={cls({ label, wide, className })}>
      <span>{label}</span>
      <input
        type="number"
        {...input}
        value={Number.isFinite(value) ? value : ''}
        onChange={e => onValue(e.target.value === '' ? 0 : Number(e.target.value))}
      />
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function SelectField<T extends string>({
  label,
  hint,
  wide,
  className,
  value,
  onValue,
  options,
  ...rest
}: Wrap &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'value' | 'onChange'> & {
    value: T;
    onValue: (v: T) => void;
    options: Array<[T, string]> | T[];
  }) {
  const opts = options.map(o => (Array.isArray(o) ? o : [o, o])) as Array<[T, string]>;
  return (
    <label className={cls({ label, wide, className })}>
      <span>{label}</span>
      <select {...rest} value={value} onChange={e => onValue(e.target.value as T)}>
        {opts.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function TextAreaField({
  label,
  hint,
  wide,
  className,
  onValue,
  ...rest
}: Wrap & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & { onValue?: (v: string) => void }) {
  return (
    <label className={cls({ label, wide, className })}>
      <span>{label}</span>
      <textarea rows={3} {...rest} onChange={e => (onValue ? onValue(e.target.value) : rest.onChange?.(e))} />
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function Check({
  label,
  checked,
  onChange,
  wide,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  wide?: boolean;
}) {
  return (
    <label className={`check${wide ? ' span2' : ''}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/** Botões segmentados (escolha única). */
export function Seg<T extends string>({
  value,
  onChange,
  options,
  className,
  col,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode; sub?: ReactNode; disabled?: boolean }>;
  className?: string;
  /** opções empilhadas com descrição */
  col?: boolean;
}) {
  return (
    <div className={`seg${col ? ' seg-col' : ''} ${className ?? ''}`} role="radiogroup">
      {options.map(o => (
        <button
          type="button"
          key={o.value}
          role="radio"
          aria-checked={value === o.value}
          className={value === o.value ? 'on' : ''}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.sub ? (
            <>
              <b>{o.label}</b>
              <small>{o.sub}</small>
            </>
          ) : (
            o.label
          )}
        </button>
      ))}
    </div>
  );
}
